import { useCallback, useSyncExternalStore } from "react";

const STORAGE_KEY = "experimental_features";

/**
 * Per-browser opt-ins. The AI Analysis (EXP) tab used to live here; it is now
 * an account-level flag on the organization, since the tab has to be visible to
 * every underwriter on the account and drives paid automatic runs.
 */
export interface ExperimentalFlags {
  positionFinder: boolean;
}

const DEFAULTS: ExperimentalFlags = { positionFinder: false };

function read(): ExperimentalFlags {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw);
    // Only known keys: browsers still hold an `aiAnalysisExp` value from before
    // that flag moved to the organization, and it must not leak back in.
    return { positionFinder: parsed?.positionFinder === true };
  } catch {
    return DEFAULTS;
  }
}

function subscribe(callback: () => void) {
  window.addEventListener("storage", callback);
  return () => window.removeEventListener("storage", callback);
}

let snapshot = read();
let snapshotRaw = typeof localStorage !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;

function getSnapshot(): ExperimentalFlags {
  const currentRaw = localStorage.getItem(STORAGE_KEY);
  if (currentRaw !== snapshotRaw) {
    snapshotRaw = currentRaw;
    snapshot = read();
  }
  return snapshot;
}

export function useExperimentalFeatures(): [
  ExperimentalFlags,
  (next: Partial<ExperimentalFlags>) => void,
] {
  const flags = useSyncExternalStore(subscribe, getSnapshot, () => DEFAULTS);
  const setFlags = useCallback((next: Partial<ExperimentalFlags>) => {
    const merged = { ...read(), ...next };
    const serialized = JSON.stringify(merged);
    localStorage.setItem(STORAGE_KEY, serialized);
    snapshotRaw = serialized;
    snapshot = merged;
    window.dispatchEvent(new StorageEvent("storage", { key: STORAGE_KEY }));
  }, []);
  return [flags, setFlags];
}
